import React, { useState } from "react";
import { useTypstCompiler } from "@/hooks/useTypstCompiler";
import { useToast } from "@/components/ui/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SignatureModal } from "@/components/shared/SignatureModal";
import { SendForSignatureModal } from "@/components/shared/SendForSignatureModal";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id, Doc } from "../../../../../convex/_generated/dataModel";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Scale, Eye, Clock, Trash2, Loader2, PenTool, Send, FileDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface BidServiceAgreementsTabProps {
  bidId: Id<"bids">;
}

export function BidServiceAgreementsTab({ bidId }: BidServiceAgreementsTabProps) {
  const { toast } = useToast();
  const documents = useQuery(api.documents.listDocumentsByBid, { bidId });
  const navigate = useNavigate();
  const deleteDocumentMutation = useMutation(api.documents.deleteDocument);
  const finalizeDocument = useMutation(api.documents.finalizeDocument);
  const { compile, isReady } = useTypstCompiler();

  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSavingPdfId, setIsSavingPdfId] = useState<string | null>(null);

  // Send for signature modal state
  const [isSendForSignatureModalOpen, setIsSendForSignatureModalOpen] = useState(false);

  const handleSaveAsPdf = async (doc: Doc<"documents">) => {
    if (!isReady) {
      toast({ title: "PDF Compiler is not ready yet. Please wait a moment.", variant: "destructive" });
      return;
    }
    
    if (!doc.typstContent && !doc.content && !doc.latexContent) {
       toast({ title: "Document has no content to compile.", variant: "destructive" });
       return;
    }

    setIsSavingPdfId(doc._id);
    try {
      const contentToCompile = doc.typstContent || doc.content || doc.latexContent || "";
      const pdfBlob = await compile(contentToCompile);
      
      if (!pdfBlob) {
        throw new Error("Compilation returned null");
      }

      const base64DataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(pdfBlob);
      });
      const base64 = base64DataUrl.split(',')[1];

      await finalizeDocument({
        documentId: doc._id,
        pdfBlob: base64,
      });
      toast({ title: "PDF Saved successfully." });
    } catch (error) {
      console.error("Save PDF Error:", error);
      toast({ title: "Failed to save PDF. Please try again.", variant: "destructive" });
    } finally {
      setIsSavingPdfId(null);
    }
  };

  // Filter for Service Agreements
  const serviceAgreements =
    documents?.filter(
      (doc: Doc<"documents">) =>
        doc.title.toLowerCase().includes("agreement") ||
        doc.title.toLowerCase().includes("contract")
    ) || [];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
      case "finalized_ready_for_signature":
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Ready</Badge>;
      case "signing_pending":
        return (
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
            Pending Signature
          </Badge>
        );
      case "signed":
        return <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">Signed</Badge>;
      case "generating":
      case "finalizing":
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Processing</Badge>;
      case "error":
      case "declined":
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Error</Badge>;
      default:
        return <Badge variant="secondary">Draft</Badge>;
    }
  };

  const handleViewDocument = (doc: Doc<"documents">) => {
    navigate("/document-writer", {
      state: {
        loadThreadId: doc.threadId,
        fromChatHistory: true,
        relatedBidId: bidId,
      },
    });
  };

  const handleDeleteDocument = async () => {
    if (!documentToDelete) return;

    setIsDeleting(true);
    try {
      await deleteDocumentMutation({ documentId: documentToDelete as any });
      setDocumentToDelete(null);
    } catch (error) {
      console.error("Failed to delete document:", error);
      alert("Failed to delete document. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  if (documents === undefined) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (serviceAgreements.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 py-12 text-center">
        <Scale className="mx-auto mb-4 h-12 w-12 text-gray-400" />
        <h3 className="text-lg font-medium text-gray-900">No service agreements yet</h3>
        <p className="mb-6 mt-1 text-gray-500">
          Create a new service agreement using the "Create Service Agreement" button above.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Agreement Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {serviceAgreements.map((doc: Doc<"documents">) => (
              <TableRow key={doc._id}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <Scale className="h-4 w-4 text-purple-600" />
                    {doc.title}
                  </div>
                </TableCell>
                <TableCell>{getStatusBadge(doc.status)}</TableCell>
                <TableCell className="text-gray-500">
                  <div className="flex items-center gap-1 text-xs">
                    <Clock className="h-3 w-3" />
                    {formatDistanceToNow(doc.createdAt, { addSuffix: true })}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewDocument(doc)}
                      className="h-8 w-8 p-0"
                      title="View Document"
                    >
                      <Eye className="h-4 w-4" />
                      <span className="sr-only">View</span>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog
        open={!!documentToDelete}
        onOpenChange={(open) => !open && setDocumentToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the agreement and remove it
              from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDocument}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
